import { useState, useEffect } from 'react';
import { ConnectionDialog } from '@/components/ConnectionDialog/ConnectionDialog';
import { ConsoleHeader } from '@/components/Console/ConsoleHeader';
import { ConsoleFooter } from '@/components/Console/ConsoleFooter';
import { VisualizationArea } from '@/components/Console/VisualizationArea';
import { LogsPanel } from '@/components/Console/LogsPanel';
import { PTTController } from '@/components/PTT/PTTController';
import { Toaster } from '@/components/ui/toaster';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useConsole } from '@/contexts/ConsoleContext';
import { useElevenLabs } from '@/hooks/useElevenLabs';
import { useToast } from '@/hooks/use-toast';
import { ConsoleSection } from '@/types';

function App() {
  const { config, isConfigured, updateConfig } = useAppConfig();
  const { isConnected, agentStatus, onAgentAction, onConsoleUpdate, onAgentError, clearConsole } = useWebSocket();
  const { sections, logs, setSections, appendSections, addLog, clearAll } = useConsole();
  const { playTTS } = useElevenLabs();
  const { toast } = useToast();
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [agentMessage, setAgentMessage] = useState<string>('');
  const [lastTTSText, setLastTTSText] = useState<string>('');
  const [lastTTSTime, setLastTTSTime] = useState<number>(0);
  const [processedEventIds, setProcessedEventIds] = useState<Set<string>>(new Set());

  // Helper function to check if event was already processed (deduplication)
  const isEventProcessed = (timestamp: number, content: string): boolean => {
    const eventId = `${timestamp}-${content.substring(0, 50)}`;
    return processedEventIds.has(eventId);
  };

  const markEventAsProcessed = (timestamp: number, content: string) => {
    const eventId = `${timestamp}-${content.substring(0, 50)}`;
    setProcessedEventIds(prev => {
      const newSet = new Set(prev);
      newSet.add(eventId);
      // Clean up old events (older than 10 seconds)
      const cutoffTime = Date.now() - 10000;
      Array.from(newSet).forEach(id => {
        const [ts] = id.split('-');
        if (parseInt(ts) < cutoffTime) {
          newSet.delete(id);
        }
      });
      return newSet;
    });
  };

  // Helper function to play TTS with deduplication
  const playTTSOnce = (text: string) => {
    const now = Date.now();
    // Skip if same text was played within last 2 seconds
    if (text === lastTTSText && now - lastTTSTime < 2000) {
      console.log('🔇 TTS skipped (duplicate):', text.substring(0, 50));
      return;
    }
    setLastTTSText(text);
    setLastTTSTime(now);
    playTTS(text);
  };

  // Show connection dialog if not configured or not connected
  useEffect(() => {
    if (!isConfigured || !isConnected) {
      setShowConnectionDialog(true);
    }
  }, [isConfigured, isConnected]);

  // Setup WebSocket event handlers
  useEffect(() => {
    // Handle agent.action events
    const unsubscribeAction = onAgentAction((event) => {
      // Note: Main logging is done in WebSocketContext.tsx

      // Skip "result" type events completely
      if (event.type === 'result') {
        return;
      }

      // Deduplicate events based on timestamp and content
      const eventContent = event.content || JSON.stringify(event.payload);
      if (isEventProcessed(event.timestamp, eventContent)) {
        console.log('🔁 Duplicate event skipped:', event.type, eventContent.substring(0, 30));
        return;
      }
      markEventAsProcessed(event.timestamp, eventContent);

      // Handle tool_use event for mcp__VOA__UpdateConsole
      if (event.type === 'tool_use') {
        const payload = event.payload as any;
        if (payload?.name === 'mcp__VOA__UpdateConsole') {
          const input = payload.input || {};
          const sections = input.sections || [];
          const action = input.action || 'replace'; // Default to 'replace' if not specified
          const message = input.message;

          console.log('🎨 UpdateConsole tool_use:', { action, sectionsCount: sections.length, message });

          // Handle camera control actions
          if (action === 'turn-camera-on') {
            console.log('📷 Agent requested: turn-camera-on');
            updateConfig({ cameraEnabled: true });
            console.log('📷 ✅ Camera config updated to ON by agent');
            // Don't return - continue processing sections if any
          } else if (action === 'turn-camera-off') {
            console.log('📷 Agent requested: turn-camera-off');
            updateConfig({ cameraEnabled: false });
            console.log('📷 ✅ Camera config updated to OFF by agent');
            // Don't return - continue processing sections if any
          }

          // Convert sections to ConsoleSection format
          const consoleSections: ConsoleSection[] = sections.map((section: any): ConsoleSection => {
            if (section.type === 'markdown' || section.type === 'mermaid' || section.type === 'html') {
              return {
                type: section.type,
                data: { content: section.content }
              } as ConsoleSection;
            } else if (section.type === 'chart') {
              return {
                type: 'chart',
                data: {
                  title: section.title || '',
                  chartType: section.chartType || 'bar',
                  labels: section.labels || [],
                  values: section.values || []
                }
              } as ConsoleSection;
            } else if (section.type === 'iframe') {
              return {
                type: 'iframe',
                data: { url: section.url, height: section.height }
              } as ConsoleSection;
            }
            return section as ConsoleSection;
          });

          // Update visualization based on action
          if (action === 'replace') {
            setSections(consoleSections);
          } else if (action === 'append') {
            appendSections(consoleSections);
          }

          // Update agent message if provided
          if (message) {
            setAgentMessage(message);
            // TTS for message
            playTTSOnce(message);
          }

          // Add log
          addLog({
            type: 'system',
            content: `Console updated (${action}): ${sections.length} sections`,
            timestamp: event.timestamp,
          });

          return; // Skip normal message handling
        }
      }

      // Extract message from payload
      const payload = event.payload as any;
      let messageContent = payload?.content || payload?.message || event.content || '';

      // Filter error messages containing API errors or provider names
      const contentLower = messageContent.toLowerCase();
      const hasError = contentLower.includes('api error:') ||
                       contentLower.includes('claude') ||
                       contentLower.includes('anthropic');

      if (hasError) {
        messageContent = 'Agent gặp bất ổn 🙁, vui lòng thử lại...';
        // Update event content for logging
        event.content = messageContent;
      }

      // Check if this is a status message (contains ⌛🔑⚡)
      const isStatusMessage = messageContent.includes('⌛') &&
        messageContent.includes('🔑') &&
        messageContent.includes('⚡');

      // Only update agentMessage if NOT a status message
      if (messageContent && !isStatusMessage) {
        setAgentMessage(messageContent);
      }

      // Add to logs (status messages will be added but rendered differently)
      addLog({
        type: 'agent',
        content: messageContent || event.content,
        timestamp: event.timestamp,
        metadata: event.payload as unknown as Record<string, unknown>,
      });

      // TTS for message type (skip status messages and error messages)
      if (event.type === 'message' && !isStatusMessage && !hasError && messageContent) {
        // Replace "No response requested" with Vietnamese message
        const ttsText = messageContent === 'No response requested'
          ? 'Em đã xong rồi nhé!'
          : messageContent;
        playTTSOnce(ttsText);
      }
    });

    // Handle console.update events
    const unsubscribeConsole = onConsoleUpdate((event) => {
      // Note: Main logging is done in WebSocketContext.tsx

      // Update visualization sections
      setSections(event.sections);

      // Update agent message if provided
      if (event.message) {
        setAgentMessage(event.message);
      }

      // Add log
      addLog({
        type: 'system',
        content: `Console updated with ${event.sections.length} sections`,
        timestamp: new Date(event.timestamp).getTime(),
      });

      // TTS for message
      if (event.message) {
        playTTSOnce(event.message);
      }
    });

    // Handle agent.error events
    const unsubscribeError = onAgentError((event) => {
      // Note: Main logging is done in WebSocketContext.tsx

      addLog({
        type: 'error',
        content: event.message,
        timestamp: event.timestamp,
        metadata: { code: event.code, details: event.details },
      });

      toast({
        title: 'Agent Error',
        description: event.message,
        variant: 'destructive',
      });
    });

    return () => {
      unsubscribeAction();
      unsubscribeConsole();
      unsubscribeError();
    };
  }, [onAgentAction, onConsoleUpdate, onAgentError, addLog, setSections, playTTS, toast]);

  const handleClearConsole = () => {
    clearAll();
    clearConsole();
    setAgentMessage('');
  };

  return (
    <div className="dark min-h-screen bg-background">
      {/* Connection Dialog */}
      <ConnectionDialog
        open={showConnectionDialog}
        onOpenChange={setShowConnectionDialog}
      />

      {/* Main Content */}
      <div className="h-screen flex flex-col">
        {/* Header */}
        <ConsoleHeader
          agentName={config.agentName}
          agentStatus={agentStatus}
          isConnected={isConnected}
          onSettings={() => setShowConnectionDialog(true)}
        />

        {/* Main Layout */}
        <main className="flex-1 flex overflow-hidden">
          {/* Visualization Area (70%) */}
          <div className="flex-[7] p-6 border-r workbench-dots flex flex-col">
            <VisualizationArea
              sections={sections}
              onClear={handleClearConsole}
              agentMessage={agentMessage}
            />
          </div>

          {/* Logs Panel (30%) */}
          <div className="flex-[3] p-6 overflow-y-auto">
            <LogsPanel logs={logs} />
          </div>
        </main>

        {/* Footer */}
        <ConsoleFooter />
      </div>

      {/* PTT Controller (Space key handler + floating button) */}
      <PTTController />

      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
}

export default App;
